import {Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators} from '@angular/forms';
import {CommonService} from '@service/common/common.service';
import {forkJoin, interval, Subject} from 'rxjs';
import {map, take, takeUntil} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute, Router} from '@angular/router';
import {UserManagementService} from '@service/user-management/user-management.service';
import {getObjectForIdInList} from '@interface/multi-select-object';
import {CompanyType} from '@enum/common/company-type';


@Component({
    selector: 'app-free-signup',
    templateUrl: './free-signup.component.html',
    styleUrls: ['./free-signup.component.scss'],
    standalone: false
})
export class FreeSignupComponent implements OnInit, OnDestroy {
  public signupForm: UntypedFormGroup = this.fb.group({
    name: ['', Validators.required],
    surname: ['', Validators.required],
    email: ['', Validators.required],
    phone: ['', Validators.required],
    jobTitleId: ['', Validators.required],
    password: ['', Validators.required],
    confirmPassword: [null, [Validators.required, (): ValidationErrors | null =>
      this.signupForm?.get('password')?.value === this.signupForm?.get('confirmPassword')?.value ? null : {notSamePassword: true}
    ]],
    company: [],
    countryId: [],
    city: [],
    zip: [],
    policy: this.fb.array([]),
    sourceWebsite: [],
    completeSourceLink: [],
    subscriptionMailchimp: [true, [Validators.required, (): ValidationErrors | null =>
      this.signupForm?.get('subscriptionMailchimp')?.value === true ? null : {require: true}
    ]]
  });
  public selectionList: any = {
    country: [],
    jobTitle: []
  };
  @ViewChild('registrationSuccessTemplate') registrationSuccessTemplate!: TemplateRef<any>;
  public countDown!: number;
  public policyFormArray = this.signupForm.get('policy') as UntypedFormArray;
  public showCompany = false;
  private destroy$ = new Subject();
  public isLoading = false;

  constructor(
    private fb: UntypedFormBuilder,
    private common: CommonService,
    private dialog: MatDialog,
    private route: Router,
    private userManagementService: UserManagementService,
    private activatedRouter: ActivatedRoute
  ) {
    this.prepareSelections();
    this.checkPassword();
    this.updateFormWithQueryParams();
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  prepareSelections(): void {
    const getJobTitle$ = this.common.getJobTitles();
    const country$ = this.common.countryListB2c();
    const policy$ = this.common.getPolicyList('privacy_policy', 'cpq');
    forkJoin({
      countryResp: country$,
      jobTitleResp: getJobTitle$,
      policyResp: policy$
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({countryResp, jobTitleResp, policyResp}) => {
        this.selectionList.country = (countryResp as any);
        this.selectionList.jobTitle = jobTitleResp as any;
        policyResp.forEach((policy: any) => {
          this.policyFormArray.push(this.fb.group({
            id: [policy.id],
            description: [policy.content],
            value: [false, (policy.isRequired ? [Validators.requiredTrue] : null)]
          }));
        });
        this.signupForm.updateValueAndValidity();
      });
  }

  private checkPassword(): void {
    this.signupForm.get('password')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.signupForm.get('confirmPassword')?.updateValueAndValidity();
    });
  }

  public updateFormWithQueryParams(): void {
    this.activatedRouter.queryParams.pipe(takeUntil(this.destroy$)).subscribe(({source_website, complete_source_link}) => {
      this.signupForm.patchValue({
        sourceWebsite: (decodeURIComponent(source_website) || document.location.hostname),
        completeSourceLink: (decodeURIComponent(complete_source_link) || document.location.origin),
      });
    });
  }

  openSuccessModal(): void {
    if (this.registrationSuccessTemplate) {
      const limit = 5;
      this.countDown = limit;
      const registrationSuccessModal = this.dialog.open(this.registrationSuccessTemplate, {
        disableClose: true
      });
      interval(1000)
        .pipe(
          map(() => this.countDown -= 1),
          take(limit),
          takeUntil(this.destroy$)
        )
        .subscribe({
          complete: () => {
            registrationSuccessModal.close();
            const redirect = this.signupForm.get('completeSourceLink')?.value;
            if (redirect !== document.location.origin) {
              window.location.href = redirect.split(/download/i)[0];
            } else {
              this.route.navigate(['login']);
            }
          }
        });
    }
  }

  submitSignupForm(): void {
    const {
      confirmPassword,
      ...data
    } = Object.assign({}, this.signupForm.getRawValue());
    this.isLoading = true;
    this.userManagementService.signupCustomer(data).pipe(takeUntil(this.destroy$)).subscribe((resp) => {
      this.isLoading = false;
      if (resp?.error) {
      } else {
        this.openSuccessModal();
      }
    });
  }

  isValidForm(): boolean {
    return this.signupForm.valid;
  }

  updateCompanyValidators(): void {
    const companyControl = this.signupForm.get('company');
    const jobTitleIdControl = this.signupForm.get('jobTitleId');
    const jobSelected = getObjectForIdInList(jobTitleIdControl?.value, this.selectionList.jobTitle);
    if (jobSelected?.companyType === CompanyType.Company) {
      companyControl?.setValidators(Validators.required);
      this.showCompany = true;
    } else {
      companyControl?.setValidators(null);
      companyControl?.setValue('');
      this.showCompany = false;
    }
    companyControl?.updateValueAndValidity();
  }
}
